/* eslint-disable react/jsx-props-no-spreading */
import { useMemo, useCallback } from "react";

import IconButton from "@mui/material/IconButton";
import {
  CheckCircleIcon,
  Edit01Icon,
  Trash01Icon,
  XCircleIcon,
} from "components";
import { message } from "contexts";
import i18n from "translations/i18n";

interface ActionIconProps extends React.ComponentProps<typeof IconButton> {
  name: "edit" | "delete" | "active" | "inactive";
  onClick: () => void;
  confirmTitle?: string;
  confirmDescription?: string;
  disabled?: boolean;
  sx?: React.ComponentProps<typeof IconButton>["sx"];
}

const ActionIcon: React.FC<ActionIconProps> = ({
  name,
  onClick,
  confirmTitle,
  confirmDescription,
  sx,
  ...innerProps
}) => {
  const icon = useMemo(() => {
    switch (name) {
      case "edit":
        return <Edit01Icon fontSize="small" />;
      case "delete":
        return <Trash01Icon fontSize="small" />;
      case "active":
        return <CheckCircleIcon fontSize="small" color="success" />;
      case "inactive":
        return <XCircleIcon fontSize="small" color="error" />;
      default:
        return <Trash01Icon fontSize="small" />;
    }
  }, [name]);

  const wrapperTitle = useMemo(() => {
    if (confirmTitle) return confirmTitle;
    switch (name) {
      case "edit":
        return i18n.t("edit");
      case "delete":
        return i18n.t("delete");
      default:
        return "";
    }
  }, [name, confirmTitle]);

  const wrapperDescription = useMemo(() => {
    if (confirmDescription) return confirmDescription;
    switch (name) {
      case "edit":
        return i18n.t("areYouSureYouWantToEdit");
      case "delete":
        return i18n.t("areYouSureYouWantToDelete");
      default:
        return "";
    }
  }, [name, confirmDescription]);

  const handleConfirm = useCallback(() => {
    if (name === "delete") {
      message({
        title: wrapperTitle,
        content: wrapperDescription,
        buttonLabel: i18n.t("confirm"),
        type: "Warning",
        callback: onClick,
      });
    } else {
      onClick();
    }
  }, [name, wrapperTitle, wrapperDescription, onClick]);

  return (
    <IconButton onClick={handleConfirm} sx={sx} {...innerProps}>
      {icon}
    </IconButton>
  );
};

export default ActionIcon;
